import { Box, Button, Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { MdsRedoOutlined } from "react-icons-with-materialsymbols/mds";
import { useParams } from "react-router-dom";

import { useShowToast } from "@/components/toast";
import { useAppDispatch, useAppSelector } from "@/reduxHooks";
import { formatTimestamp } from "@/utils/date-convertor";

import { workflowManagerApi } from "../../api";
import { useRestoreWorkflowVersionMutation } from "../../api/workflow-api";
import { useSyncWorkflow } from "../../hooks/useSyncWorkflow";
import {
  currentPreviewVersion,
  hideAllPanels,
  setPreviewMode,
  setPreviewVersion,
  triggerVersionHistoryRefresh,
} from "../../redux";
import { ReactFlowInstanceContext } from "../flow-editor";

const PreviewModeOptions = () => {
  const [isRestored, setIsRestored] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const params = useParams();
  const dispatch = useAppDispatch();
  const toast = useShowToast(undefined, undefined, true);
  const { setNodesFn, setEdgesFn, editorInstance } = useContext(
    ReactFlowInstanceContext
  )!;
  const { refetchWorkflow } = useSyncWorkflow();
  const previewVersion = useAppSelector(currentPreviewVersion);
  const [restoreWorkflowVersion] = useRestoreWorkflowVersionMutation();

  useEffect(() => {
    setIsRestored(false);
  }, [previewVersion?.versionTagId]);

  const parseDescription = (description: string | null) => {
    if (!description) return null
    try {
      const parsed = JSON.parse(description)
      if (!parsed.message) return description
      let message = parsed.message
      if (Array.isArray(parsed.variables)) {
        parsed.variables.forEach((variable: { name: string; value: string; type?: string }) => {
          let value = variable.value
          if (variable.type === 'timestamp') value = formatTimestamp(value)
          message = message.replace(`{${variable.name}}`, value)
        })
      }
      return message
    } catch {
      return description
    }
  }

  const title = useMemo(() => {
    if (!previewVersion?.versionMetaData || previewVersion?.isCurrentVersion) {
      return "current version"
    }
    if (isEmpty(previewVersion?.versionMetaData?.description)) {
      return formatTimestamp(previewVersion?.versionMetaData?.createdOn)
    }
    return parseDescription(previewVersion?.versionMetaData?.description) || formatTimestamp(previewVersion?.versionMetaData?.createdOn)
  }, [previewVersion])

  const handleRestoreWorkflowVersion = async () => {
    if (!previewVersion?.versionMetaData) {
      return;
    }
    try {
      setIsRestoring(true);
      const result = await restoreWorkflowVersion({
        workflowId: params.editorId!,
        wfRunId: previewVersion?.versionMetaData.id,
      });

      dispatch(
        workflowManagerApi.util.updateQueryData(
          "getWorkflowVersions",
          { workflowId: params.editorId! },
          () => ({
            response: {
              data: {
                runs: [],
              },
              pagination: {
                next: null,
                count: 0,
                previous: null,
              },
            },
          })
        )
      );

      if (result.data) {
        await refetchWorkflow(setNodesFn, setEdgesFn, editorInstance);
        dispatch(triggerVersionHistoryRefresh());
        toast({
          title: "Version restored",
          description: `The ${title} version has been restored`,
          status: "success",
        });
      }
      setIsRestored(true);
    } catch (error) {
      toast({
        title: "Error restoring version",
        description: "Failed to restore version",
        status: "error",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCloseRestore = () => {
    setIsRestored(false);
    dispatch(hideAllPanels());
    dispatch(
      setPreviewVersion({
        versionMetaData: null,
        workflow: null,
        versionTagId: null,
        isCurrentVersion: false,
      })
    );
  };

  if (isRestored) {
    return (
      <Flex className="text-sm" align="center">
        <Box className="py-2 mx-2">Version restored !</Box>
        <Button colorScheme="dark" onClick={handleCloseRestore} size={"sm"}>
          Done
        </Button>
      </Flex>
    );
  }

  return (
    <Flex className="text-sm" align="center">
      <Box className="py-2 mx-2">
        Showing&nbsp;
        <b>{title}</b>
      </Box>
      {previewVersion?.versionMetaData && !previewVersion?.isCurrentVersion && (
        <Button
          className="disabled:!bg-dark-400 disabled:!opacity-100"
          colorScheme="dark"
          isLoading={isRestoring}
          leftIcon={<MdsRedoOutlined strokeWidth={22} size={14} />}
          loadingText="Restoring..."
          onClick={handleRestoreWorkflowVersion}
          size="sm"
        >
          Restore this version
        </Button>
      )}
    </Flex>
  );
};

export default PreviewModeOptions;
